import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { logoutWithCheckout } from "../utils/logoutWithCheckout";
import {
  getSessionIdleTimeoutMs,
  touchSessionActivity,
  getSessionIdleMs,
  SESSION_LAST_ACTIVITY_KEY,
} from "../utils/sessionTimeout";

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];
const CHECK_INTERVAL_MS = 15000;
const THROTTLE_MS = 1000;

export function useIdleSessionTimeout(isAuthenticated, logout, user) {
  const navigate = useNavigate();
  const logoutRef = useRef(logout);
  const userRef = useRef(user);
  const isExpiringRef = useRef(false);
  const lastThrottleRef = useRef(0);

  logoutRef.current = logout;
  userRef.current = user;

  const expireSession = useCallback(async () => {
    if (isExpiringRef.current || !logoutRef.current) return;
    isExpiringRef.current = true;

    toast.info("Your session has expired due to inactivity. Please log in again.");

    await logoutWithCheckout(() => logoutRef.current());

    navigate("/login", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      isExpiringRef.current = false;
      return undefined;
    }

    touchSessionActivity();
    isExpiringRef.current = false;

    const onActivity = () => {
      const now = Date.now();
      if (now - lastThrottleRef.current < THROTTLE_MS) return;
      lastThrottleRef.current = now;
      touchSessionActivity();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true });
    });

    const intervalId = setInterval(() => {
      if (getSessionIdleMs() >= getSessionIdleTimeoutMs()) {
        expireSession();
      }
    }, CHECK_INTERVAL_MS);

    const onStorage = (event) => {
      if (event.key === SESSION_LAST_ACTIVITY_KEY && event.newValue) {
        return;
      }
      if (event.key === "user" && !event.newValue && logoutRef.current) {
        isExpiringRef.current = true;
        logoutRef.current();
        navigate("/login", { replace: true });
      }
    };

    window.addEventListener("storage", onStorage);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity);
      });
      clearInterval(intervalId);
      window.removeEventListener("storage", onStorage);
    };
  }, [isAuthenticated, expireSession, navigate]);
}
